import { useEffect, useState } from 'preact/hooks';
import {
  DashboardApi,
  HttpError,
  type InviteSummary,
  type MemberRole,
  type MemberSummary,
} from '../api.js';
import type { AuthOrg, AuthUser } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

const ROLE_LABELS: Record<MemberRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
  api: 'API',
};

const HUMAN_ROLES: Array<'owner' | 'admin' | 'editor' | 'viewer'> = [
  'owner',
  'admin',
  'editor',
  'viewer',
];

export function Members({
  user,
  org,
}: {
  user: AuthUser;
  org: AuthOrg;
}): preact.ComponentChildren {
  const [members, setMembers] = useState<MemberSummary[] | null>(null);
  const [invites, setInvites] = useState<InviteSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'editor' | 'viewer'>('editor');
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const [m, i] = await Promise.all([api.listMembers(org.id), api.listInvites(org.id)]);
      setMembers(m.members);
      setInvites(i.invites);
      setError(null);
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh closes over org.id only
  useEffect(() => {
    void refresh();
  }, [org.id]);

  const me = members?.find((m) => m.self) ?? null;
  const myRole: MemberRole = me?.role ?? 'viewer';
  const canManage = myRole === 'owner' || myRole === 'admin';

  const invite = async () => {
    setCreating(true);
    try {
      await api.createInvite(org.id, { email: inviteEmail.trim(), role: inviteRole });
      setInviteEmail('');
      await refresh();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  const revokeInvite = async (id: string) => {
    if (!confirm('Revoke this invite? The link in the email will stop working.')) return;
    try {
      await api.revokeInvite(org.id, id);
      await refresh();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  const changeRole = async (m: MemberSummary, role: MemberRole) => {
    try {
      await api.updateMemberRole(org.id, m.userId, role);
      await refresh();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  const removeMember = async (m: MemberSummary) => {
    if (!confirm(`Remove ${m.email} from ${org.name}?`)) return;
    try {
      await api.removeMember(org.id, m.userId);
      await refresh();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  return (
    <>
      <div class="page-header">
        <h1>Members</h1>
        <p>
          Invite teammates and assign roles. Owners can manage billing and ownership; admins can
          manage members and API keys; editors can author content; viewers are read-only.
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      {canManage && (
        <div class="card">
          <h2>Invite a teammate</h2>
          <div class="field">
            <label htmlFor="invite-email">Email</label>
            <input
              id="invite-email"
              type="email"
              value={inviteEmail}
              placeholder="teammate@company.com"
              onInput={(e) => setInviteEmail((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="field">
            <label htmlFor="invite-role">Role</label>
            <select
              id="invite-role"
              value={inviteRole}
              onChange={(e) =>
                setInviteRole(
                  (e.target as HTMLSelectElement).value as 'admin' | 'editor' | 'viewer',
                )
              }
            >
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <button
            type="button"
            disabled={creating || !inviteEmail.trim()}
            onClick={() => void invite()}
          >
            {creating ? 'Sending…' : 'Send invitation'}
          </button>
          <p class="small muted" style={{ marginTop: 8 }}>
            We'll email a one-time link that expires in 7 days. The recipient must sign in with the
            same email to accept.
          </p>
        </div>
      )}

      <div class="card">
        <h2>Current members</h2>
        {members === null ? (
          <p class="muted">
            <output>Loading…</output>
          </p>
        ) : (
          <table class="audit">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Joined</th>
                <th>{/* actions */}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.userId}>
                  <td>
                    {m.email}
                    {m.self && (
                      <span class="kind-pill" style={{ marginLeft: 8 }}>
                        you
                      </span>
                    )}
                  </td>
                  <td>{m.displayName ?? '—'}</td>
                  <td>
                    {canManage && !m.self ? (
                      <select
                        aria-label={`Role for ${m.email}`}
                        value={m.role}
                        onChange={(e) =>
                          void changeRole(m, (e.target as HTMLSelectElement).value as MemberRole)
                        }
                      >
                        {HUMAN_ROLES.filter((r) => r !== 'owner' || myRole === 'owner').map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      ROLE_LABELS[m.role]
                    )}
                  </td>
                  <td>{new Date(m.joinedAt).toLocaleDateString()}</td>
                  <td>
                    {canManage && !m.self && (
                      <button type="button" class="secondary" onClick={() => void removeMember(m)}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {canManage && (
        <div class="card">
          <h2>Pending invitations</h2>
          {invites === null ? (
            <p class="muted">
              <output>Loading…</output>
            </p>
          ) : invites.length === 0 ? (
            <p class="muted">No pending invitations.</p>
          ) : (
            <table class="audit">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Sent</th>
                  <th>Expires</th>
                  <th>{/* actions */}</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((i) => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td>{ROLE_LABELS[i.role]}</td>
                    <td>{new Date(i.createdAt).toLocaleDateString()}</td>
                    <td>{new Date(i.expiresAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        type="button"
                        class="secondary"
                        onClick={() => void revokeInvite(i.id)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <p class="small muted">Signed in as {user.email}.</p>
    </>
  );
}
